import { ChangeDetectorRef, Component, DOCUMENT, inject, Input, OnDestroy, OnInit, ChangeDetectionStrategy, output, signal } from '@angular/core';
import { ContentBlock, TeeTimeActionPayload, TeeTimeCourse, TeeTimeListData, TeeTimePickerData, TeeTimePill, TeeTimeSlot } from '../../../model/chat.model';
import { DecimalPipe } from '@angular/common';
import { NgApexchartsModule, ApexChart, ApexTooltip, ApexDataLabels, ApexLegend, ApexXAxis, ApexYAxis, ApexGrid } from 'ng-apexcharts';
import { FormatChatPipe } from '../../../pipes/format-chat/format-chat.pipe';
import { BookTeeTimeComponent } from '../../tee-times/book-tee-time/book-tee-time.component';
import { TeeTimeDetailComponent } from '../../tee-times/tee-time-detail/tee-time-detail.component';
import { WaitlistFormComponent } from '../../tee-times/waitlist-form/waitlist-form.component';
import { AlertFormComponent } from '../../tee-times/alert-form/alert-form.component';
import { AlertConfirmationComponent } from '../../tee-times/alert-confirmation/alert-confirmation.component';
import { ProfileVerificationRequiredComponent } from '../profile-verification-required/profile-verification-required.component';
import { COUNTRIES } from '../../../utils/country-const';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-block-elements',
  imports: [
    NgApexchartsModule,
    FormatChatPipe,
    DecimalPipe,
    BookTeeTimeComponent,
    TeeTimeDetailComponent,
    WaitlistFormComponent,
    AlertFormComponent,
    AlertConfirmationComponent,
    ProfileVerificationRequiredComponent
  ],
  templateUrl: './block-elements.component.html',
  styleUrl: './block-elements.component.scss'
})
export class BlockElementsComponent implements OnInit, OnDestroy {
  @Input() contentBlocks: ContentBlock[] | undefined = [];
  @Input() loading: boolean = false;
  @Input() interactive: boolean = true;
  readonly slotSelected = output<TeeTimeActionPayload>();
  readonly viewMoreSlots = output<TeeTimeActionPayload>();
  protected readonly slotPreviewCount = 5;
  private readonly expandedCourses = signal(new Set<string>());
  /** locationId of the course currently shown in detail view (carousel) — null = course list view */
  protected readonly openCourseId = signal<string | null>(null);
  chartColors: string[] = ['#1C1D1F', '#3A3B3D', '#56585A', '#787A7D'];
  chartOptions: {
    chart?: ApexChart;
    tooltip?: ApexTooltip;
    dataLabels?: ApexDataLabels;
    xaxis?: ApexXAxis;
    yaxis?: ApexYAxis;
    grid?: ApexGrid;
    legend?: ApexLegend;
  } = {};

  private document = inject(DOCUMENT);
  private _cdr = inject(ChangeDetectorRef);
  private themeObserver: MutationObserver | undefined;

  ngOnInit() {
    this.updateChartTheme();
    this.themeObserver = new MutationObserver(() => {
      this.updateChartTheme();
      this._cdr.markForCheck();
    });
    this.themeObserver.observe(this.document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });
  }

  private updateChartTheme() {
    const isDark = false;
    const textColor = isDark ? '#FFFFFF' : '#1C1D1F';

    this.chartColors = isDark
      ? ['#FFFFFF', '#C8C8C8', '#A0A0A0', '#787878']
      : ['#1C1D1F', '#3A3B3D', '#56585A', '#787A7D'];

    this.chartOptions = {
      chart: {
        type: 'bar',
        foreColor: textColor,
        toolbar: {
          tools: {
            download: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="${textColor}" stroke-width="2"><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="18" x2="21" y2="18"/></svg>`
          }
        }
      } as ApexChart,
      tooltip: {
        theme: isDark ? 'dark' : 'light'
      } as ApexTooltip,
      dataLabels: {
        style: {
          colors: [isDark ? '#1C1D1F' : '#FFFFFF']
        },
        background: {
          enabled: false
        }
      } as ApexDataLabels,
      xaxis: {
        labels: { style: { colors: textColor } }
      } as ApexXAxis,
      yaxis: {
        labels: { style: { colors: textColor } }
      } as ApexYAxis,
      grid: {
        borderColor: isDark ? '#333333' : '#e0e0e0'
      } as ApexGrid,
      legend: {
        labels: { colors: textColor }
      } as ApexLegend
    };
  }

  ngOnDestroy() {
    this.themeObserver?.disconnect();
  }

  /** Country-name -> ISO alpha-2 lookup, used to resolve a flag when countryCode is absent. */
  private readonly countryNameToAlpha2 = new Map<string, string>(
    COUNTRIES.map((c) => [c.name.toLowerCase(), c.alpha2]),
  );

  /** UK constituent countries have their own flag-icons codes but no ISO alpha-2. */
  private readonly subRegionFlags = new Map<string, string>([
    ['scotland', 'gb-sct'],
    ['england', 'gb-eng'],
    ['wales', 'gb-wls'],
    ['northern ireland', 'gb-nir'],
  ]);

  /**
   * flag-icons country code for a course (e.g. 'us', 'gb-sct'), derived from its ISO
   * 3166-1 alpha-2 `countryCode`, with fallbacks on the country name and the UK
   * constituent countries. Returns '' when no flag can be resolved.
   */
  protected getCountryFlagCode(course: TeeTimeCourse): string {
    const name = (course.country ?? '').trim().toLowerCase();
    const sub = this.subRegionFlags.get(name);
    if (sub) return sub;
    let code = (course.countryCode ?? '').trim().toLowerCase();
    if (!/^[a-z]{2}$/.test(code)) {
      code = (this.countryNameToAlpha2.get(name) ?? '').toLowerCase();
    }
    return /^[a-z]{2}$/.test(code) ? code : '';
  }

  protected isCourseExpanded(locationId: string): boolean {
    return this.expandedCourses().has(locationId);
  }

  protected toggleCourse(locationId: string): void {
    this.expandedCourses.update((current) => {
      const next = new Set(current);
      if (next.has(locationId)) {
        next.delete(locationId);
      } else {
        next.add(locationId);
      }
      return next;
    });
  }

  /** Flattens the new teeTimes.{morning,afternoon} arrays — falls back to legacy `slots`. */
  protected getCourseSlots(course: TeeTimeCourse): TeeTimePill[] {
    if (course.teeTimes) {
      return [
        ...(course.teeTimes.morning ?? []),
        ...(course.teeTimes.afternoon ?? []),
      ];
    }
    return (course.slots ?? []) as TeeTimePill[];
  }

  protected visibleSlotCount(course: TeeTimeCourse): number {
    const slots = this.getCourseSlots(course);
    if (this.isCourseExpanded(course.locationId)) {
      return slots.length;
    }
    return Math.min(this.slotPreviewCount, slots.length);
  }

  protected remainingSlotCount(course: TeeTimeCourse): number {
    const total = course.slotCount ?? this.getCourseSlots(course).length;
    return Math.max(0, total - this.visibleSlotCount(course));
  }

  protected hasMoreSlots(course: TeeTimeCourse): boolean {
    return this.remainingSlotCount(course) > 0;
  }

  protected onSelectSlot(course: TeeTimeCourse, slot: TeeTimePill, listData: TeeTimeListData): void {
    const selection = this.resolveSelection(listData, slot);
    this.slotSelected.emit({
      question: `Select ${course.name}`,
      action: {
        type: 'select_course',
        courseName: course.name,
        locationId: course.locationId,
        ...selection,
        preferredTime: slot.time,
      },
    });
  }

  /** Whole-card click — switch the list block to the inline detail view for this course (carousel). No chat round-trip. */
  protected onSelectCourse(course: TeeTimeCourse): void {
    if (!this.interactive) return;
    this.openCourseId.set(course.locationId);
  }

  protected getCurrentCourse(listData: TeeTimeListData): TeeTimeCourse | null {
    const id = this.openCourseId();
    if (!id) return null;
    return listData.courses.find(c => c.locationId === id) ?? null;
  }

  protected getCurrentCourseIndex(listData: TeeTimeListData): number {
    const id = this.openCourseId();
    if (!id) return -1;
    return listData.courses.findIndex(c => c.locationId === id);
  }

  protected hasPrevCourse(listData: TeeTimeListData): boolean {
    return this.getCurrentCourseIndex(listData) > 0;
  }

  protected hasNextCourse(listData: TeeTimeListData): boolean {
    const idx = this.getCurrentCourseIndex(listData);
    return idx >= 0 && idx < listData.courses.length - 1;
  }

  protected prevCourse(listData: TeeTimeListData): void {
    const idx = this.getCurrentCourseIndex(listData);
    if (idx > 0) this.openCourseId.set(listData.courses[idx - 1].locationId);
  }

  protected nextCourse(listData: TeeTimeListData): void {
    const idx = this.getCurrentCourseIndex(listData);
    if (idx >= 0 && idx < listData.courses.length - 1) {
      this.openCourseId.set(listData.courses[idx + 1].locationId);
    }
  }

  protected closeCourseDetail(): void {
    this.openCourseId.set(null);
  }

  /** Build the picker payload BookTeeTimeComponent expects from a course + the list's selection. */
  protected buildPickerData(course: TeeTimeCourse, listData: TeeTimeListData): TeeTimePickerData {
    const sel = listData.selection;
    const courseLocation =
      course.city && course.country
        ? `${course.city}, ${course.country}`
        : course.city ?? course.country ?? undefined;
    return {
      priceRange: course.priceRange,
      courseName: course.name,
      locationId: course.locationId,
      courseLocation,
      countryCode: course.countryCode,
      availableTimes: {
        morning: course.teeTimes?.morning ?? [],
        afternoon: course.teeTimes?.afternoon ?? [],
      },
      availability: course.availability,
      selection: {
        date: sel?.date ?? '',
        players: sel?.players ?? null,
        holes: sel?.holes ?? null,
        courseName: course.name,
        locationId: course.locationId,
        courseLocation,
        countryCode: course.countryCode,
        place: sel?.place,
        coordinates: sel?.coordinates,
        radiusKm: sel?.radiusKm,
        // Forward the echoed exact-time context so a per-course "Alert me" click
        // preserves what the user originally typed in submit_booking_form.
        ...(sel?.appointmentTime ? { appointmentTime: sel.appointmentTime } : {}),
        ...(sel?.preferredTime ? { preferredTime: sel.preferredTime } : {}),
        // Carry the course's TeeFox capability so select_time can thread it forward
        // and booking-review can gate the prepay option.
        ...(course.supportedRequestTypes ? { supportedRequestTypes: course.supportedRequestTypes } : {}),
      },
    };
  }

  protected onPickerTimeSelected(payload: TeeTimeActionPayload): void {
    this.slotSelected.emit(payload);
  }

  protected onViewMoreSlots(course: TeeTimeCourse, listData: TeeTimeListData): void {
    const selection = this.resolveSelection(listData);
    this.viewMoreSlots.emit({
      question: `Select ${course.name}`,
      action: {
        type: 'select_course',
        courseName: course.name,
        locationId: course.locationId,
        ...selection,
      },
    });
    if (this.getCourseSlots(course).length > this.visibleSlotCount(course)) {
      this.toggleCourse(course.locationId);
    }
  }

  private resolveSelection(listData: TeeTimeListData, slot?: TeeTimeSlot) {
    if (listData.selection) {
      return listData.selection;
    }
    return {
      date: slot?.apptTime?.slice(0, 10) ?? listData.date ?? '',
      players: slot?.players ?? (Number(listData.players) || 0),
      holes: slot?.holes ?? (Number(listData.holes) || 0),
      preferredTime: slot?.time ?? '',
    };
  }
}
